import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Download, FileText, RefreshCw, Check, X, Loader2, Landmark, Eye, EyeOff, Archive, ArchiveRestore } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip as RechartsTooltip } from "recharts";
import { useToast } from "@/hooks/use-toast";
import { useBrandColors } from "@/lib/brand-colors";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PdfPreview } from "@/components/pdf-preview";

interface MercuryStatementRow {
  id: number;
  mercuryAccountId: string;
  mercuryAccountName: string | null;
  statementMonth: string;
  startDate: string | null;
  endDate: string | null;
  fileName: string;
  totalDeposits: string | null;
  totalWithdrawals: string | null;
  endingBalance: string | null;
  archivedAt: string | null;
  downloadedAt: string;
}

function fmtStatementAmount(val: string | null): string {
  if (!val) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

function fmtDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return "—";
  const fmt = (d: string) => {
    const dt = new Date(d + "T12:00:00");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return `From ${fmt(start)}`;
  return `Through ${fmt(end!)}`;
}

type StatementSortKey = "account" | "period" | "deposits" | "withdrawals" | "endingBalance";
type SortDir = "asc" | "desc";

const BALANCE_CHART_EXTRA_COLORS = ["#10B981", "#8B5CF6", "#EF4444", "#0EA5E9", "#F59E0B"];

function formatStatementMonthLabel(month: string): string {
  if (!month) return month;
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

interface BalanceTrendRow {
  mercuryAccountId: string;
  mercuryAccountName: string | null;
  statementMonth: string;
  endingBalance: string | null;
}

interface BalanceChartProps {
  rows: BalanceTrendRow[];
}

const BALANCE_CHART_RANGE_OPTIONS: { value: string; label: string; months: number | null }[] = [
  { value: "6", label: "6m", months: 6 },
  { value: "12", label: "12m", months: 12 },
  { value: "24", label: "24m", months: 24 },
  { value: "all", label: "All", months: null },
];
const BALANCE_CHART_RANGE_STORAGE_KEY = "padpulse.statementBalanceChart.range";

function StatementBalanceChart({ rows }: BalanceChartProps) {
  const brandColors = useBrandColors();
  const balanceChartColors = [brandColors.brand, brandColors.brandAccent, ...BALANCE_CHART_EXTRA_COLORS];
  const [range, setRange] = useState<string>(() => {
    if (typeof window === "undefined") return "12";
    const stored = window.sessionStorage.getItem(BALANCE_CHART_RANGE_STORAGE_KEY);
    if (stored && BALANCE_CHART_RANGE_OPTIONS.some((o) => o.value === stored)) return stored;
    return "12";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(BALANCE_CHART_RANGE_STORAGE_KEY, range);
  }, [range]);

  const { accounts, data } = useMemo(() => {
    const accountMap = new Map<string, string>();
    const monthSet = new Set<string>();
    const valueMap = new Map<string, Map<string, number>>();

    for (const r of rows) {
      const bal = r.endingBalance ? parseFloat(r.endingBalance) : NaN;
      if (isNaN(bal)) continue;
      const accountId = r.mercuryAccountId;
      const accountName = r.mercuryAccountName ?? r.mercuryAccountId;
      if (!accountMap.has(accountId)) accountMap.set(accountId, accountName);
      monthSet.add(r.statementMonth);
      if (!valueMap.has(r.statementMonth)) valueMap.set(r.statementMonth, new Map());
      valueMap.get(r.statementMonth)!.set(accountId, bal);
    }

    const allMonths = Array.from(monthSet).sort();
    const rangeMonths = BALANCE_CHART_RANGE_OPTIONS.find((o) => o.value === range)?.months ?? null;
    const months = rangeMonths !== null ? allMonths.slice(-rangeMonths) : allMonths;
    const accounts = Array.from(accountMap.entries()).map(([id, name]) => ({ id, name }));
    const data = months.map((month) => {
      const row: Record<string, string | number> = { month, label: formatStatementMonthLabel(month) };
      const perAccount = valueMap.get(month);
      for (const acc of accounts) {
        const v = perAccount?.get(acc.id);
        if (typeof v === "number") row[acc.id] = v;
      }
      return row;
    });

    return { months, accounts, data };
  }, [rows, range]);

  if (data.length < 2 || accounts.length === 0) return null;

  const currencyFormatter = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="statements-balance-chart">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Ending balance trend</h2>
          <p className="text-sm text-gray-500">Month-end balance per account</p>
        </div>
        <div
          className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5"
          role="group"
          aria-label="Balance trend range"
          data-testid="balance-chart-range-picker"
        >
          {BALANCE_CHART_RANGE_OPTIONS.map((opt) => {
            const active = range === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRange(opt.value)}
                className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-colors ${
                  active
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
                aria-pressed={active}
                data-testid={`button-balance-chart-range-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={false}
              tickLine={false}
              width={70}
              tickFormatter={(v: number) =>
                Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`
              }
            />
            <RechartsTooltip
              contentStyle={{ fontSize: 12, padding: "6px 10px" }}
              formatter={(val: number, name: string) => [currencyFormatter(val), name]}
              labelFormatter={(label) => String(label)}
            />
            {accounts.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {accounts.map((acc, idx) => (
              <Line
                key={acc.id}
                type="monotone"
                dataKey={acc.id}
                name={acc.name}
                stroke={balanceChartColors[idx % balanceChartColors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const STATEMENTS_PAGE_SIZE = 50;

interface MercuryStatementsResponse {
  rows: MercuryStatementRow[];
  total: number;
  limit: number;
  offset: number;
}

export function StatementsTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tokenInput, setTokenInput] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [viewStatement, setViewStatement] = useState<MercuryStatementRow | null>(null);
  const [accountFilter, setAccountFilterState] = useState<string>(
    () => localStorage.getItem("pp:financials-statements-account-filter") ?? "all",
  );
  const setAccountFilter = (next: string) => {
    localStorage.setItem("pp:financials-statements-account-filter", next);
    setAccountFilterState(next);
  };
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [sortKey, setSortKey] = useListState<StatementSortKey>("statements:sort", "period");
  const [sortDir, setSortDir] = useListState<SortDir>("statements:direction", "desc");
  const [page, setPage] = useState(0);
  const [archiveConfirm, setArchiveConfirm] = useState<MercuryStatementRow | null>(null);
  // Task #2304 — set to true when a "Check now" attempt reveals the main
  // key doesn't have statements scope, so we can fall back to the token form.
  const [showTokenFallback, setShowTokenFallback] = useState(false);

  // Task #1944 — filters/sort/pagination now happen server-side. Reset to
  // page 0 whenever any filter or sort changes so the user never lands on
  // an empty later page.
  useEffect(() => {
    setPage(0);
  }, [showArchived, accountFilter, dateFrom, dateTo, sortKey, sortDir]);

  const connQuery = useQuery<any>({ queryKey: ["/api/app/mercury-connection"] });

  const accountsQuery = useQuery<Array<{ id: string; name: string | null }>>({
    queryKey: ["/api/app/mercury-statements/accounts"],
  });

  const statementsQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (showArchived) params.set("archived", "true");
    if (accountFilter !== "all") params.set("accountId", accountFilter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    params.set("sort", sortKey);
    params.set("dir", sortDir);
    params.set("limit", String(STATEMENTS_PAGE_SIZE));
    params.set("offset", String(page * STATEMENTS_PAGE_SIZE));
    return params.toString();
  }, [showArchived, accountFilter, dateFrom, dateTo, sortKey, sortDir, page]);

  const statementsQuery = useQuery<MercuryStatementsResponse>({
    queryKey: ["/api/app/mercury-statements", statementsQueryParams],
    queryFn: async () => {
      const res = await fetch(`/api/app/mercury-statements?${statementsQueryParams}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // Task #1955 — the trend chart needs to reflect every statement matching
  // the current account / date / archived filters, not just the current page.
  // We key this query only on the filters (not page or sort order) so flipping
  // pages or resorting the table doesn't refetch or change the chart.
  const balanceTrendQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (showArchived) params.set("archived", "true");
    if (accountFilter !== "all") params.set("accountId", accountFilter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    return params.toString();
  }, [showArchived, accountFilter, dateFrom, dateTo]);

  const balanceTrendQuery = useQuery<{ rows: BalanceTrendRow[] }>({
    queryKey: ["/api/app/mercury-statements/balance-trend", balanceTrendQueryParams],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/mercury-statements/balance-trend?${balanceTrendQueryParams}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const saveTokenMutation = useMutation({
    mutationFn: async (apiToken: string) =>
      apiRequest("PUT", "/api/app/mercury-connection/api-token", { apiToken }),
    onSuccess: () => {
      toast({ title: "Mercury API token saved", description: "Backfilling last 24 months in the background." });
      setTokenInput("");
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements/balance-trend"] });
    },
    onError: (err: unknown) => toast({ title: "Failed to save token", description: parseErrorMessage(err, "Couldn't save your Mercury token. Please try again."), variant: "destructive" }),
  });

  const refreshMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", "/api/app/mercury-statements/refresh").then((r) => r.json()),
    onSuccess: (data: any) => {
      toast({ title: "Statements synced", description: `Added ${data?.created ?? 0} new statement(s).` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements/balance-trend"] });
    },
    onError: (err: unknown) => {
      // Task #2304 — when the main Mercury key lacks statements scope, the
      // backend returns 422 with code "statements_scope_missing". Surface a
      // clear message and reveal the dedicated token input form so the host
      // can paste a statements-scoped token without needing to re-connect.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("statements_scope_missing")) {
        setShowTokenFallback(true);
        toast({
          title: "Statements key needed",
          description:
            "Your Mercury API key doesn't have statements access. " +
            "Paste a dedicated statements token below to enable this feature.",
          variant: "destructive",
          duration: 10000,
        });
        return;
      }
      toast({ title: "Sync failed", description: parseErrorMessage(err, "Couldn't sync statements. Please try again."), variant: "destructive" });
    },
  });

  // Task #2310 — Manual backfill trigger for hosts whose statements were
  // never backfilled (e.g. they skipped the second token step). Uses the
  // main Mercury key as a fallback per Task #2304.
  const backfillStatementsMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", "/api/app/mercury-statements/backfill"),
    onSuccess: () => {
      toast({ title: "Backfill started", description: "Pulling the last 24 months of statements in the background." });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements/balance-trend"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("statements_scope_missing")) {
        setShowTokenFallback(true);
        toast({
          title: "Statements key needed",
          description:
            "Your Mercury API key doesn't have statements access. " +
            "Paste a dedicated statements token below to enable this feature.",
          variant: "destructive",
          duration: 10000,
        });
        return;
      }
      toast({ title: "Backfill failed", description: parseErrorMessage(err, "Couldn't start the backfill. Please try again."), variant: "destructive" });
    },
  });

  const removeTokenMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", "/api/app/mercury-connection/api-token"),
    onSuccess: () => {
      toast({ title: "API token removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
    },
    onError: (err: unknown) => toast({ title: "Failed to remove token", description: parseErrorMessage(err, "Couldn't remove your Mercury token. Please try again."), variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/app/mercury-statements/${id}/archive`, {}),
    onSuccess: () => {
      toast({ title: "Statement archived" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements"] });
    },
    onError: (err: unknown) => toast({ title: "Failed to archive", description: parseErrorMessage(err, "Couldn't archive this statement. Please try again."), variant: "destructive" }),
  });

  const restoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/app/mercury-statements/${id}/restore`, {}),
    onSuccess: () => {
      toast({ title: "Statement restored" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-statements"] });
    },
    onError: (err: unknown) => toast({ title: "Failed to restore", description: parseErrorMessage(err, "Couldn't restore this statement. Please try again."), variant: "destructive" }),
  });

  const hasConn = !!connQuery.data;
  const hasToken = !!connQuery.data?.hasApiToken;
  // Task #2304 — the main Mercury connection key (set during the initial
  // connect flow) can also be used for statements, so we don't need a
  // second token prompt when it's present.
  const hasMainKey = !!connQuery.data?.hasApiKey;

  const rows = statementsQuery.data?.rows ?? [];
  const total = statementsQuery.data?.total ?? 0;
  const accountOptions = useMemo(
    () =>
      (accountsQuery.data ?? []).map((a) => ({ id: a.id, name: a.name ?? a.id })),
    [accountsQuery.data],
  );

  if (connQuery.isLoading) {
    return <div className="text-sm text-gray-500" data-testid="statements-loading">Loading…</div>;
  }

  if (!hasConn) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="statements-empty">
        <h2 className="text-lg font-semibold text-gray-900">Connect Mercury first</h2>
        <p className="mt-1 text-sm text-gray-600">
          Set up your Mercury webhook connection on the Settings tab, then come back here to add your API token.
        </p>
      </div>
    );
  }

  const toggleSort = (key: StatementSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "account" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key: StatementSortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const hasFilters = accountFilter !== "all" || !!dateFrom || !!dateTo;

  return (
    <div className="space-y-6" data-testid="statements-tab">
      {/* API Token card — Task #2304: hidden when main connection key covers statements */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Landmark className="h-5 w-5 text-blue-600" /> Mercury Statements
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {hasToken
                ? "Statements sync using your saved Mercury API token. Last 24 months are back-filled on save; new statements sync monthly."
                : hasMainKey
                  ? "Statements sync using your saved Mercury connection key. No separate token needed."
                  : "Connect Mercury in the Settings tab to enable the monthly statements archive."}
            </p>
          </div>
          {hasToken && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => removeTokenMutation.mutate()}
              disabled={removeTokenMutation.isPending}
              data-testid="button-remove-mercury-api-token"
            >
              Remove token
            </Button>
          )}
        </div>

        {/* When a dedicated statements token is saved, show replace form + refresh */}
        {hasToken && (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Input
                type={showToken ? "text" : "password"}
                placeholder="API token saved — paste a new one to replace"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                data-testid="input-mercury-api-token"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                onClick={() => setShowToken((s) => !s)}
                aria-label={showToken ? "Hide token" : "Show token"}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              onClick={() => saveTokenMutation.mutate(tokenInput.trim())}
              disabled={!tokenInput.trim() || saveTokenMutation.isPending}
              data-testid="button-save-mercury-api-token"
            >
              {saveTokenMutation.isPending ? "Saving…" : "Replace token"}
            </Button>
            <Button
              variant="outline"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              data-testid="button-refresh-mercury-statements"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
              Check now
            </Button>
          </div>
        )}

        {/* Task #2304 — when the main connection key is present and hasn't
            yet failed for statements, show a compact status line + refresh
            button. If a "Check now" attempt reveals the key lacks statements
            scope, showTokenFallback flips true and we drop to the token form. */}
        {!hasToken && hasMainKey && !showTokenFallback && (
          <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-green-100 bg-green-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-green-800">
              <svg className="h-4 w-4 text-green-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
              <span data-testid="text-statements-using-saved-key">Using your saved Mercury key</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => backfillStatementsMutation.mutate()}
                disabled={backfillStatementsMutation.isPending}
                data-testid="button-backfill-mercury-statements"
              >
                {backfillStatementsMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Backfilling…
                  </>
                ) : (
                  "Backfill 24 months"
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                data-testid="button-refresh-mercury-statements"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
                {refreshMutation.isPending ? "Syncing…" : "Check now"}
              </Button>
            </div>
          </div>
        )}

        {/* Token input form — shown when:
            (a) no key at all, or
            (b) the main key was tried but lacks statements scope. */}
        {!hasToken && (!hasMainKey || showTokenFallback) && (
          <div className="mt-4 space-y-2">
            {showTokenFallback && (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="text-statements-scope-warning"
              >
                <svg className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <span>
                  Your Mercury API key doesn't have statements access. In Mercury, go to{" "}
                  <strong>Settings → API Tokens</strong>, re-create the token with{" "}
                  <strong>Statements</strong> access enabled, then paste it here.
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Input
                  type={showToken ? "text" : "password"}
                  placeholder="secret-token-..."
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  data-testid="input-mercury-api-token"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowToken((s) => !s)}
                  aria-label={showToken ? "Hide token" : "Show token"}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                onClick={() => saveTokenMutation.mutate(tokenInput.trim())}
                disabled={!tokenInput.trim() || saveTokenMutation.isPending}
                data-testid="button-save-mercury-api-token"
              >
                {saveTokenMutation.isPending ? "Saving…" : "Save & backfill"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Ending balance trend — pulls every statement matching the
          current filters (not just the visible page) so the chart stays
          stable when hosts paginate or resort the table. (Task #1955) */}
      <StatementBalanceChart rows={balanceTrendQuery.data?.rows ?? []} />

      {/* Statements list */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Monthly statements</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={showArchived}
                onCheckedChange={setShowArchived}
                id="toggle-archived"
                data-testid="toggle-show-archived"
              />
              <label htmlFor="toggle-archived" className="text-sm text-gray-600 cursor-pointer select-none">
                Show archived
              </label>
            </div>
            <span className="text-sm text-gray-500" data-testid="text-statements-count">
              {total} statement{total === 1 ? "" : "s"}
              {hasFilters ? " match" : ""}
            </span>
          </div>
        </div>

        {(accountOptions.length > 0 || hasFilters) && (
          <div className="flex flex-wrap items-end gap-3 px-6 py-3 border-b border-gray-100 bg-gray-50/60">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account</label>
              <Select value={accountFilter} onValueChange={setAccountFilter}>
                <SelectTrigger className="h-9 w-[220px] bg-white" data-testid="select-statement-account-filter">
                  <SelectValue placeholder="All accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accountOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide" htmlFor="statement-date-from">From</label>
              <Input
                id="statement-date-from"
                type="date"
                className="h-9 w-[170px] bg-white"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="input-statement-date-from"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide" htmlFor="statement-date-to">To</label>
              <Input
                id="statement-date-to"
                type="date"
                className="h-9 w-[170px] bg-white"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="input-statement-date-to"
              />
            </div>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9"
                onClick={() => { setAccountFilter("all"); setDateFrom(""); setDateTo(""); }}
                data-testid="button-clear-statement-filters"
              >
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}

        {statementsQuery.isLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500" data-testid="statements-empty-list">
            {hasFilters
              ? "No statements match the current filters."
              : showArchived
              ? "No archived statements."
              : hasToken
              ? "No statements yet — the backfill may still be running."
              : "Add your API token above to start the backfill."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-6 py-3 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center font-medium uppercase tracking-wide hover:text-gray-900"
                      onClick={() => toggleSort("account")}
                      data-testid="sort-statement-account"
                    >
                      Account{sortIndicator("account")}
                    </button>
                  </th>
                  <th className="px-6 py-3 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center font-medium uppercase tracking-wide hover:text-gray-900"
                      onClick={() => toggleSort("period")}
                      data-testid="sort-statement-period"
                    >
                      Period{sortIndicator("period")}
                    </button>
                  </th>
                  <th className="px-6 py-3 font-medium">Date range</th>
                  <th className="px-6 py-3 font-medium text-right">
                    <button
                      type="button"
                      className="inline-flex items-center font-medium uppercase tracking-wide hover:text-gray-900"
                      onClick={() => toggleSort("deposits")}
                      data-testid="sort-statement-deposits"
                    >
                      Deposits{sortIndicator("deposits")}
                    </button>
                  </th>
                  <th className="px-6 py-3 font-medium text-right">
                    <button
                      type="button"
                      className="inline-flex items-center font-medium uppercase tracking-wide hover:text-gray-900"
                      onClick={() => toggleSort("withdrawals")}
                      data-testid="sort-statement-withdrawals"
                    >
                      Withdrawals{sortIndicator("withdrawals")}
                    </button>
                  </th>
                  <th className="px-6 py-3 font-medium text-right">
                    <button
                      type="button"
                      className="inline-flex items-center font-medium uppercase tracking-wide hover:text-gray-900"
                      onClick={() => toggleSort("endingBalance")}
                      data-testid="sort-statement-ending-balance"
                    >
                      Ending balance{sortIndicator("endingBalance")}
                    </button>
                  </th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-testid={`row-statement-${row.id}`}>
                    <td className="px-6 py-3 text-gray-900 font-medium truncate max-w-[180px]">
                      {row.mercuryAccountName ?? row.mercuryAccountId}
                    </td>
                    <td className="px-6 py-3 text-gray-700 whitespace-nowrap">{row.statementMonth}</td>
                    <td className="px-6 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDateRange(row.startDate, row.endDate)}</td>
                    <td className="px-6 py-3 text-right text-gray-700 tabular-nums">{fmtStatementAmount(row.totalDeposits)}</td>
                    <td className="px-6 py-3 text-right text-gray-700 tabular-nums">{fmtStatementAmount(row.totalWithdrawals)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-gray-900 tabular-nums">{fmtStatementAmount(row.endingBalance)}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setViewStatement(row)}
                                data-testid={`button-view-statement-${row.id}`}
                              >
                                <Eye className="h-4 w-4 text-gray-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View PDF</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`/api/app/mercury-statements/${row.id}/download`}
                                download={row.fileName}
                                className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-gray-100 transition-colors"
                                data-testid={`link-download-statement-${row.id}`}
                              >
                                <Download className="h-4 w-4 text-gray-500" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>Download PDF</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {showArchived ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => restoreMutation.mutate(row.id)}
                                  disabled={restoreMutation.isPending}
                                  data-testid={`button-restore-statement-${row.id}`}
                                >
                                  <ArchiveRestore className="h-4 w-4 text-gray-500" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setArchiveConfirm(row)}
                                  disabled={archiveMutation.isPending}
                                  data-testid={`button-archive-statement-${row.id}`}
                                >
                                  <Archive className="h-4 w-4 text-gray-500" />
                                </Button>
                              )}
                            </TooltipTrigger>
                            <TooltipContent>{showArchived ? "Restore" : "Archive"}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > STATEMENTS_PAGE_SIZE && (
          <div
            className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50/60 text-sm text-gray-600"
            data-testid="statements-pagination"
          >
            <span data-testid="text-statements-page-range">
              {total === 0
                ? "0 of 0"
                : `${page * STATEMENTS_PAGE_SIZE + 1}–${Math.min(
                    (page + 1) * STATEMENTS_PAGE_SIZE,
                    total,
                  )} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || statementsQuery.isFetching}
                data-testid="button-statements-prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * STATEMENTS_PAGE_SIZE >= total || statementsQuery.isFetching}
                data-testid="button-statements-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* PDF viewer dialog */}
      <Dialog open={!!viewStatement} onOpenChange={(open) => { if (!open) setViewStatement(null); }}>
        <DialogContent className="max-w-3xl w-full h-[85vh] flex flex-col p-0" data-testid="dialog-statement-pdf">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-200 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <FileText className="h-4 w-4 text-blue-600" />
              {viewStatement?.mercuryAccountName ?? viewStatement?.mercuryAccountId} — {viewStatement?.statementMonth}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            {viewStatement && (
              <PdfPreview
                file={`/api/app/mercury-statements/${viewStatement.id}/file`}
                openInNewTabHref={`/api/app/mercury-statements/${viewStatement.id}/file`}
                testIdPrefix="statement-pdf"
                className="h-full"
              />
            )}
          </div>
          <div className="shrink-0 px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
            {viewStatement && (
              <a
                href={`/api/app/mercury-statements/${viewStatement.id}/download`}
                download={viewStatement.fileName}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                data-testid="button-dialog-download-statement"
              >
                <Download className="h-4 w-4" /> Download PDF
              </a>
            )}
            <Button variant="outline" size="sm" onClick={() => setViewStatement(null)} data-testid="button-close-pdf-dialog">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!archiveConfirm}
        onOpenChange={(open) => { if (!open) setArchiveConfirm(null); }}
      >
        <AlertDialogContent data-testid="dialog-confirm-archive-statement">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this statement?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be hidden from the default view. You can bring it back later by toggling "Show archived."
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-archive-statement">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-archive-statement"
              onClick={() => {
                if (archiveConfirm) {
                  archiveMutation.mutate(archiveConfirm.id);
                  setArchiveConfirm(null);
                }
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

